import { ColDef, IRowNode } from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import {
  Button,
  CircularProgress,
  CollapsibleCard,
  ConfirmationDialogBox,
  DatePicker,
  Switch,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import {
  columnsToolPanel,
  filtersToolPanel,
} from '@components/Grid/components/constants';
import { getRowGroupStyle } from '@components/Grid/utils';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import {
  AutoComplete,
  Notification,
  RadioGroup,
  SelectInput,
} from '@components';
import { CarrierAutoCompleteModel } from '@typeDefinitions/index';
import {
  createCarrierRestriction,
  deleteCarrierRestriction,
  fetchCarrierRestrictionReasonCodes,
  updateCarrierRestriction,
} from '@store/customers/customerDetails/services';
import {
  fetchCarrierRestrictionsAction,
  updateCarrierRestrictionStatusAction,
} from '@store/customers/customerDetails/actions';
import AtgGifLoader from '@common/AtgGifLoader';
import { carrierRestrictionsStore } from '@store/customers/customerDetails/store';
import { ToastType } from '@components/Toast';
import { handleToast } from '@utils/utils';
import { useParams } from '@solidjs/router';
import { CarrierRestrictionReasonCodeT } from '@store/customers/customerDetails/types';
import { createForm } from '@felte/solid';
import {
  CloseCircleIcon,
  CloseIcon,
  PencilIcon,
  TruckIcon,
} from '@atgfr8/icons';
import { validator } from '@felte/validator-yup';
import {
  createEffect,
  createMemo,
  createSignal,
  on,
  onMount,
  Show,
} from 'solid-js';
import { DateTime } from 'luxon';

import {
  carrierRestrictionFormSchema,
  CarrierRestrictionsFormState,
} from './validations';

const CARRIER_RESRICTION_NOTIFCATION = (
  <div class="flex flex-col">
    <div class="text-[#026EA1]">
      Restrict specific carriers from being assigned to this customer.
    </div>
    <div>
      Choose Hard Block to prevent assignment or Warning to allow assignment
      after user acknowledgement.
    </div>
  </div>
);

const cellStyle = {
  padding: '10px',
  'word-break': 'break-word',
  'white-space': 'normal',
  'line-height': '1.6 ',
};

const DELETE_RESTRICTION_DIALOG_ID = 'carrier-restriction-delete-dialog';

type CarrierRestrictionRecord = CarrierRestrictionsFormState & {
  id: number;
  status: 'Active' | 'Inactive';
};

const RESTRICTION_TYPE_SEVERITY: Record<
  CarrierRestrictionsFormState['restrictionType'],
  number
> = {
  Warning: 1,
  'Hard Block': 2,
};

const severityToRestrictionType = (
  severity: number,
): CarrierRestrictionsFormState['restrictionType'] =>
  severity === RESTRICTION_TYPE_SEVERITY['Hard Block']
    ? 'Hard Block'
    : 'Warning';

const toDateInput = (value: string | null) => (value ? value.slice(0, 10) : '');

const CarrierRestrictions = () => {
  const params = useParams();
  const [gridRef, setGridRef] = createSignal<AgGridSolidRef | null>(null);
  //   const fetchClaims = async () => {
  //     gridRef()?.api.showLoadingOverlay();
  //     if (params.id) {
  //       const res = await getCustomerClaimsHistory(params.id);
  //       res && setClaimHistory(res);
  //     }
  //     gridRef()?.api.hideOverlay();
  //   };

  //   let previousId = params.id;
  //   createEffect(async () => {
  //     if (previousId !== params.id) {
  //       previousId = params.id;
  //       await fetchClaims();
  //     }
  //   });

  const [reasonCodes, setReasonCodes] = createSignal<
    CarrierRestrictionReasonCodeT[]
  >([]);

  onMount(async () => {
    try {
      const codes = await fetchCarrierRestrictionReasonCodes();
      setReasonCodes([...codes].sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (error: unknown) {
      handleToast(ToastType.Error, (error as Error).message);
    }
  });

  const reasonTypeOptions = createMemo(() =>
    reasonCodes().map((reason) => ({
      label: reason.displayName,
      value: reason.displayName,
    })),
  );

  const rowData = createMemo<CarrierRestrictionRecord[]>(() =>
    carrierRestrictionsStore.restrictions.map((item) => ({
      id: item.id,
      carrierId: item.carrierId,
      carrierName: item.carrierName,
      carrierMc: item.carrierMC ?? '',
      carrierDot: item.carrierDOT ?? '',
      restrictionType: severityToRestrictionType(item.severity),
      reasonType:
        reasonCodes().find((reason) => reason.id === item.reasonCodeId)
          ?.displayName ?? item.reasonCode,
      reasonDescription: item.message ?? '',
      effectiveFrom: toDateInput(item.effectiveFrom),
      effectiveTo: toDateInput(item.effectiveTo),
      status: item.isActive ? 'Active' : 'Inactive',
    })),
  );

  createEffect(() => {
    if (carrierRestrictionsStore.isLoading) {
      gridRef()?.api.showLoadingOverlay();
    } else {
      gridRef()?.api.hideOverlay();
    }
  });

  const [editingRowId, setEditingRowId] = createSignal<string | null>(null);
  const [rowToDelete, setRowToDelete] =
    createSignal<CarrierRestrictionRecord | null>(null);

  const { form, data, setFields, errors, reset, isSubmitting } =
    createForm<CarrierRestrictionsFormState>({
      initialValues: {
        carrierId: null,
        carrierName: '',
        carrierMc: '',
        carrierDot: '',
        restrictionType: 'Hard Block',
        effectiveFrom: '',
        effectiveTo: '',
        reasonType: '',
        reasonDescription: '',
      },
      extend: validator({
        schema: carrierRestrictionFormSchema,
      }),
      onSubmit: (values) => handleSubmit(values),
    });

  const toUtcIso = (date: string, edge: 'start' | 'end') => {
    const day = DateTime.fromISO(date, { zone: 'utc' });
    return (edge === 'start' ? day.startOf('day') : day.endOf('day'))
      .set({ millisecond: 0 })
      .toISO({ suppressMilliseconds: true }) as string;
  };

  const handleSubmit = async (values: CarrierRestrictionsFormState) => {
    const reasonCode = reasonCodes().find(
      (reason) => reason.displayName === values.reasonType,
    );
    if (!reasonCode || values.carrierId === null || !params.id) {
      handleToast(ToastType.Error, 'Select a valid carrier and reason type');
      return;
    }

    const payload = {
      carrierId: values.carrierId,
      severity: RESTRICTION_TYPE_SEVERITY[values.restrictionType],
      reasonCodeId: reasonCode.id,
      message: values.reasonDescription,
      effectiveFrom: toUtcIso(values.effectiveFrom, 'start'),
      effectiveTo: values.effectiveTo
        ? toUtcIso(values.effectiveTo, 'end')
        : null,
    };
    const rowId = editingRowId();
    const restrictionId = rowId ? Number(rowId) : null;

    try {
      const response =
        restrictionId === null
          ? await createCarrierRestriction(params.id, payload)
          : await updateCarrierRestriction(params.id, restrictionId, {
              ...payload,
              isActive:
                carrierRestrictionsStore.restrictions.find(
                  (item) => item.id === restrictionId,
                )?.isActive ?? true,
            });
      if (!response.isSuccess) {
        handleToast(ToastType.Error, 'Failed to save carrier restriction');
        return;
      }
    } catch (error: unknown) {
      handleToast(ToastType.Error, (error as Error).message);
      return;
    }

    if (restrictionId === null) {
      reset();
    } else {
      handleCancelEdit();
    }
    await fetchCarrierRestrictionsAction(params.id);
    handleToast(
      ToastType.Success,
      restrictionId === null
        ? 'Carrier restriction added'
        : 'Carrier restriction updated',
    );
  };

  const handleEditClick = (
    row: CarrierRestrictionRecord,
    node: IRowNode<CarrierRestrictionRecord>,
  ) => {
    setEditingRowId(node.id ?? null);
    setFields('carrierId', row.carrierId);
    setFields('carrierName', row.carrierName);
    setFields('carrierMc', row.carrierMc);
    setFields('carrierDot', row.carrierDot);
    setFields('restrictionType', row.restrictionType);
    setFields('effectiveFrom', row.effectiveFrom);
    setFields('effectiveTo', row.effectiveTo);
    setFields('reasonType', row.reasonType);
    setFields('reasonDescription', row.reasonDescription);
  };

  const clearCarrier = () => {
    setFields('carrierId', null);
    setFields('carrierName', '');
    setFields('carrierMc', '');
    setFields('carrierDot', '');
  };

  const handleCancelEdit = () => {
    reset();
    setEditingRowId(null);
  };

  createEffect(
    on(
      () => params.id,
      (customerId) => {
        handleCancelEdit();
        setRowToDelete(null);
        closeDialogBox(DELETE_RESTRICTION_DIALOG_ID);
        if (customerId) {
          void fetchCarrierRestrictionsAction(customerId);
        }
      },
    ),
  );

  const handleDeleteClick = (
    row: CarrierRestrictionRecord,
    node: IRowNode<CarrierRestrictionRecord>,
  ) => {
    setRowToDelete(row);
    if (node.id === editingRowId()) {
      handleCancelEdit();
    }
    openDialogBox(DELETE_RESTRICTION_DIALOG_ID);
  };

  const handleConfirmDelete = async () => {
    const row = rowToDelete();
    if (!row || !params.id) return;

    try {
      const response = await deleteCarrierRestriction(params.id, row.id);
      if (!response.isSuccess) {
        handleToast(ToastType.Error, 'Failed to delete carrier restriction');
        return;
      }
      handleToast(ToastType.Success, 'Carrier restriction deleted');
      await fetchCarrierRestrictionsAction(params.id);
    } catch (error: unknown) {
      handleToast(ToastType.Error, (error as Error).message);
    } finally {
      setRowToDelete(null);
      closeDialogBox(DELETE_RESTRICTION_DIALOG_ID);
    }
  };

  const [isStatusUpdating, setIsStatusUpdating] = createSignal(false);

  const handleStatusChange = async (
    row: CarrierRestrictionRecord,
    node: IRowNode<CarrierRestrictionRecord>,
    isActive: boolean,
  ) => {
    if (!params.id) return;
    setIsStatusUpdating(true);
    const updated = await updateCarrierRestrictionStatusAction(
      params.id,
      row.id,
      isActive,
    );
    setIsStatusUpdating(false);
    if (!updated) {
      gridRef()?.api.refreshCells({ rowNodes: [node], force: true });
    }
  };

  const columnDefs: ColDef[] = [
    {
      field: 'carrierName',
      headerName: 'Carrier',
      minWidth: 180,
      filter: 'agTextColumnFilter',
      cellStyle,
      cellRenderer: (params: { data: CarrierRestrictionRecord }) => (
        <div class="flex flex-col">
          <span class="font-semibold">{params.data.carrierName}</span>
          <span class="text-xs text-gray-500">{`MC: ${params.data.carrierMc} | DOT: ${params.data.carrierDot}`}</span>
        </div>
      ),
    },
    {
      field: 'restrictionType',
      headerName: 'Restriction Type',
      minWidth: 150,
      filter: 'agTextColumnFilter',
      cellStyle,
      cellRenderer: (params: {
        value: CarrierRestrictionRecord['restrictionType'];
      }) => (
        <span
          class={`px-2 py-1 rounded-full text-xs border ${
            params.value === 'Hard Block'
              ? 'text-[#B00020] border-[#B00020]'
              : 'text-[#FFA000] border-[#FFA000]'
          }`}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: 'reasonType',
      headerName: 'Reason Type',
      minWidth: 160,
      filter: 'agTextColumnFilter',
      cellStyle,
    },
    {
      field: 'reasonDescription',
      headerName: 'Reason Description',
      minWidth: 260,
      filter: 'agTextColumnFilter',
      cellStyle,
    },
    {
      field: 'effectiveFrom',
      headerName: 'Effective Period',
      minWidth: 160,
      filter: 'agTextColumnFilter',
      cellStyle,
      cellRenderer: (params: { data: CarrierRestrictionRecord }) => {
        const from = params.data.effectiveFrom
          ? DateTime.fromISO(params.data.effectiveFrom).toFormat('MM/dd/yyyy')
          : 'No Start Date';
        const to = params.data.effectiveTo
          ? DateTime.fromISO(params.data.effectiveTo).toFormat('MM/dd/yyyy')
          : 'No End Date';
        return <span>{`${from} - ${to}`}</span>;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 110,
      filter: 'agTextColumnFilter',
      cellStyle,
      cellRenderer: (params: {
        value: CarrierRestrictionRecord['status'];
        data: CarrierRestrictionRecord;
        node: IRowNode<CarrierRestrictionRecord>;
      }) => (
        <div class="flex items-center gap-2">
          <Switch
            checked={params.value === 'Active'}
            disabled={isStatusUpdating()}
            onChange={(checked: boolean) =>
              void handleStatusChange(params.data, params.node, checked)
            }
            dataTestId={`carrier-restriction-status-switch-${params.node.id}`}
          />
          <span
            class={
              params.value === 'Active'
                ? 'text-[#026EA1] font-medium'
                : 'text-gray-400'
            }
          >
            {params.value}
          </span>
        </div>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      autoHeight: true,
      cellStyle,
      sortable: false,
      filter: false,
      cellRenderer: (params: {
        data: CarrierRestrictionRecord;
        node: IRowNode<CarrierRestrictionRecord>;
      }) => (
        <div class="flex gap-3 items-center text-[#026EA1]">
          <PencilIcon
            style={{
              'font-size': '1.5rem',
              cursor: 'pointer',
            }}
            onClick={() => handleEditClick(params.data, params.node)}
            dataTestId={`carrier-restriction-edit-icon-${params.node.id}`}
          />
          <CloseCircleIcon
            style={{
              'font-size': '1.5rem',
              color: '#B00020',
              cursor: 'pointer',
            }}
            onClick={() => handleDeleteClick(params.data, params.node)}
            dataTestId={`carrier-restriction-delete-icon-${params.node.id}`}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <CollapsibleCard
        startTitle="Carrier Restrictions (Customer Specific)"
        class="mb-2"
      >
        <Notification
          type="info"
          style={{ 'background-color': '#E6F4F9' }}
          text={CARRIER_RESRICTION_NOTIFCATION}
        />
        <div class="add-restriction-section">
          <form ref={form}>
            <div class="text-lg font-semibold mt-2">
              {editingRowId() ? 'Update Restriction' : 'Add Restriction'}
            </div>
            <div class="grid grid-cols-12 gap-4 items-start mt-2">
              <div class="col-span-3">
                <AutoComplete<CarrierAutoCompleteModel>
                  id="carrier-list-search"
                  endpoint="carrier/autocomplete/?term"
                  label="Carrier Search"
                  required
                  placeholder="Search by Name, MC, DOT"
                  dataTestId="carrier-list-search-input"
                  responseFieldName="name"
                  value={data().carrierName || ''}
                  onItemSelect={(item) => {
                    setFields('carrierId', item.id);
                    setFields('carrierName', item.name || '');
                    setFields('carrierMc', item.mc || '');
                    setFields('carrierDot', item.dot || '');
                  }}
                  onClearValue={clearCarrier}
                  error={errors().carrierName}
                  minCharacters={0}
                />
                <Show when={data().carrierName}>
                  <div
                    class="mt-2 flex items-center gap-3 rounded bg-[#D6E2EB] px-3 py-2"
                    data-testid="selected-carrier-card"
                  >
                    <TruckIcon class="shrink-0 text-[#4A8DB7]" />
                    <div class="flex min-w-0 flex-1 flex-col">
                      <span class="truncate font-semibold uppercase text-[rgba(0,0,0,0.87)]">
                        {data().carrierName}
                      </span>
                      <span class="text-xs text-[rgba(0,0,0,0.6)]">
                        {`MC: ${data().carrierMc} | DOT: ${data().carrierDot}`}
                      </span>
                    </div>
                    <CloseIcon
                      class="shrink-0 cursor-pointer text-[#B00020]"
                      onClick={clearCarrier}
                      dataTestId="selected-carrier-clear"
                    />
                  </div>
                </Show>
              </div>
              <div class="col-span-5">
                <RadioGroup
                  label="Restriction Type"
                  required
                  value={data().restrictionType}
                  options={[
                    {
                      label: (
                        <div class="flex flex-col mt-2">
                          <span class="text-md">Hard Block</span>
                          <span class="text-xs">
                            Prevents carrier from being assigned.
                          </span>
                        </div>
                      ),
                      value: 'Hard Block',
                    },
                    {
                      label: (
                        <div class="flex flex-col mt-2">
                          <span class="text-md">Warning</span>
                          <span class="text-xs">
                            Allows assignment after user acknowledgement.
                          </span>
                        </div>
                      ),
                      value: 'Warning',
                    },
                  ]}
                  onChange={(value) => setFields('restrictionType', value)}
                  labelClass="mb-[-6px] text-xs"
                />
                <div class="grid grid-cols-2 gap-2 mt-3">
                  <DatePicker
                    required
                    label="Effective From"
                    value={data().effectiveFrom}
                    handleChange={(value: string) => {
                      setFields('effectiveFrom', value);
                    }}
                    error={errors().effectiveFrom}
                    dataTestId="effectiveFrom"
                  />
                  <div>
                    <DatePicker
                      label="Effective To"
                      optional
                      value={data().effectiveTo}
                      handleChange={(value: string) => {
                        setFields('effectiveTo', value);
                      }}
                      error={errors().effectiveTo}
                      dataTestId="effectiveTo"
                    />
                    <span class="text-xs">
                      Leave blank for an ongoing restriction.
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-span-4">
                <SelectInput
                  label="Reason Type"
                  required
                  menuItems={reasonTypeOptions()}
                  value={data().reasonType}
                  error={errors().reasonType as unknown as string}
                  onChange={(value) => {
                    setFields('reasonType', value);
                    const selected = reasonCodes().find(
                      (reason) => reason.displayName === value,
                    );
                    setFields(
                      'reasonDescription',
                      selected?.defaultMessage ?? '',
                    );
                    if (selected) {
                      setFields(
                        'restrictionType',
                        severityToRestrictionType(selected.defaultSeverity),
                      );
                    }
                  }}
                  dataTestId="flag-popup-state-select"
                />
                <div class="mt-2">
                  <TextField
                    multiline
                    label="Description"
                    value={data().reasonDescription}
                    rows={3}
                    error={errors().reasonDescription}
                    onChange={(data) => setFields('reasonDescription', data)}
                    maxLength={5000}
                  />
                </div>
              </div>
            </div>
            <div class="flex justify-end gap-2 mt-4">
              <Show when={editingRowId()}>
                <Button
                  type="button"
                  variant="outlined"
                  disabled={isSubmitting()}
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
              </Show>
              <Button
                type="submit"
                disabled={isSubmitting()}
                endIcon={
                  isSubmitting() ? (
                    <CircularProgress size={20} color="primary" />
                  ) : null
                }
              >
                {editingRowId() ? 'Update Restriction' : 'Add Restriction'}
              </Button>
            </div>
          </form>
        </div>
        <div class="ag-theme-alpine mt-4">
          <AgGridSolid
            ref={setGridRef}
            columnDefs={columnDefs}
            rowData={rowData()}
            getRowId={(row: { data: CarrierRestrictionRecord }) =>
              String(row.data.id)
            }
            domLayout="autoHeight"
            defaultColDef={{
              flex: 1,
              minWidth: 100,
              filter: true,
              resizable: true,
            }}
            animateRows={true}
            getRowStyle={getRowGroupStyle}
            sideBar={{
              toolPanels: [columnsToolPanel, filtersToolPanel],
            }}
          />
        </div>
        <Show when={isStatusUpdating()}>
          <AtgGifLoader />
        </Show>
        <ConfirmationDialogBox
          isDialogOpen={checkIfDialogBoxIsOpen(DELETE_RESTRICTION_DIALOG_ID)}
          closeDialogBox={() => {
            setRowToDelete(null);
            closeDialogBox(DELETE_RESTRICTION_DIALOG_ID);
          }}
          dataTestId="carrier-restriction-delete-dialog"
          id={DELETE_RESTRICTION_DIALOG_ID}
          title="Are you sure you want to delete this carrier restriction?"
          onSubmit={handleConfirmDelete}
          onSubmitText="Delete"
        />
      </CollapsibleCard>
    </div>
  );
};

export default CarrierRestrictions;
